import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRightLeft, ScanBarcode, PackageX, AlertTriangle, PackageCheck, Printer } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../i18n";
import { usePageTitle } from "../hooks/usePageTitle";
import { apiFetch } from "../api/apiFetch";
import { logoutProcessor } from "../components/helpers/logoutProcessor";
import { playSuccess, playError, playBoxAdd } from "../components/helpers/audioPlayer";
import { printCourierPalletLabel, type PalletPrint } from "../components/helpers/courierPalletPrint";
import PrinterSelectModal from "../components/PrinterSelectModal";
import { getSavedNetworkPrinter, isSavedNetworkPrinterOnline } from "../components/helpers/networkPrint";
import { isPrintAgentAvailable } from "../components/helpers/printAgent";

import HiddenScanInput from "../components/HiddenScanInput";
import FlowLoader from "../components/FlowLoader";
import ScanStatusIcons from "../components/ScanStatusIcons";
import ScanFlashOverlay from "../components/ScanFlashOverlay";
import FitWord from "../components/FitWord";
import "../styles/scansheet.css";
import "../styles/dispatch-unload.css";

// ---------------------------------------------------------------------------
// Dispatch › Conveyor Unload (Pembroke Dock, 2026-10-14)
//
// Parcels come off the pack-desk conveyor and go onto one pallet per
// courier. Parcels put on the wrong courier's pallet were getting lost, so
// every parcel is now checked in two scans:
//
//   1. scan the shipping label  → the screen turns the courier's colour and
//      says, in one word, which pallet it goes on (POST /dispatch/unload/
//      scan-parcel — the courier comes from OUR booking; at Newport, where
//      nothing is booked through the Portal, it is read from the label
//      barcode's shape instead — APC digits vs InPost parcel number — with
//      no order, piece or sort code: `source: "label_format"`);
//   2. scan that pallet's floor barcode → green tick (POST /dispatch/unload/
//      confirm-bay). The wrong pallet is loud, recorded, and keeps the
//      parcel pending until the right one is scanned or it is set aside.
//
// A label that must NOT be loaded (void after a reopen, a duplicate, an
// unknown label, another site's parcel) gets a full-screen card the
// operator has to dismiss, so nothing is ever guessed onto a pallet.
//
// OUTBOUND PALLETS (Alex, 2026-10-19): the floor barcode is a POSITION;
// the physical pallet on it changes several times a day. Every match lands
// on the pallet currently open on that position (opened by the first
// parcel). When the pallet is full the operator presses "Close pallet"
// and scans the position's floor barcode TWICE (Alex, 2026-10-27: no
// default position, no confirm button — the second scan is the close):
// the Portal allocates the courier's next number (POST /dispatch/unload/
// close-pallet) and the card says what the label will read ("APC - 12 ·
// 37 parcels"). Where the warehouse has "Print upon close" switched on
// (Settings › Dispatch › Courier Pallets, migration 226 — Poland first)
// the handheld prints the 6x4 label there and then, with Print again on a
// failure; elsewhere a team leader prints it from Admin › Warehouse ›
// Outbound. The forklift driver later scans the label onto the lorry
// (Dispatch › Load lorry). The label is an identifier only — it holds no
// stock.
//
// The day's reconciliation (shipped vs loaded per courier, the pallets) is
// NOT here: it is Admin > Warehouse > Outbound, Assistant Team Leader and
// above.
// ---------------------------------------------------------------------------

// pallets: false = a safety-check position (Royal Mail): the parcel must go
// there, but no pallet is tracked — nothing to close, no label.
type Bay = { barcode: string; name: string | null; carrier_code: string; sort_codes?: string[]; pallets?: boolean };

export type ParcelScan = {
    ok: boolean;
    // collection: a collection order's label — never a courier pallet.
    verdict: "ok" | "voided" | "duplicate" | "unknown_label" | "wrong_warehouse" | "collection" | "not_a_parcel";
    reason: string | null;
    // "booking" = the courier came from our carrier booking; "label_format"
    // = read off the barcode's shape (Newport), so order / piece are null.
    source?: "booking" | "label_format" | "collection";
    scan_id: number;
    order_id?: string | null;
    order_number?: string | null;
    order_warehouse?: string | null;
    carrier_code?: string | null;
    carrier_name?: string | null;
    // Which of the courier's pallets: APC's pre-sort number read off the
    // label (the number in the black block), InPost 24 / 48. Null when the
    // courier has one pallet. target_label is "APC 4" / "InPost 48".
    sort_code?: string | null;
    target_label?: string | null;
    piece?: { position: number; total: number } | null;
    bays?: Bay[];
    // movable: its pallet is not on the lorry, so the duplicate card can
    // offer "Move to another pallet".
    prior?: { at: string; by: string | null; bay_name: string | null; pallet_sku?: string | null; pallet_status?: string | null; movable?: boolean; zones?: string | null };
    looks_like?: string | null;
};

export type ClosedPallet = {
    id: number; label: string | null; status: "open" | "closed" | "loaded"; carrier_code: string; carrier_name: string;
    bay_barcode: string; bay_name: string | null; sort_codes?: string[]; parcel_count: number | null; sequence_gap: boolean;
    closed_at: string | null; closed_by: string | null; label_confirmed_at?: string | null;
    loaded_at: string | null; loaded_by: string | null; print?: PalletPrint | null;
};

type Card =
    | { kind: "voided" | "duplicate" | "unknown_label" | "wrong_warehouse" | "collection"; scan: ParcelScan }
    | { kind: "wrong_bay"; expected: string; scanned: string; bayName: string }
    // print: "leader" = printed later from Outbound; otherwise the state of
    // the print-upon-close print on this handheld.
    | { kind: "close_done"; sku: string; count: number; pallet: ClosedPallet; print: "leader" | "printing" | "printed" | "failed" }
    // The parcel went on a closed pallet (its label scanned instead of the
    // floor barcode) — said out loud so it is never a silent surprise.
    | { kind: "added_closed"; sku: string; count: number }
    // A parcel moved off one pallet onto another (from the duplicate card).
    | { kind: "moved"; from: string; to: string };

// "Close pallet" mode: which position is being closed (null until a floor
// barcode is scanned) and how many parcels are on it. Scanning the same
// floor barcode again closes it.
type Closing = { bay: Bay | null; count: number };

type SessionCounts = { byCarrier: Record<string, number>; wrong: number; aside: number; closed: number };

const readWarehouse = (): string => {
    try {
        const raw = window.localStorage.getItem("user");
        const parsed = raw ? JSON.parse(raw) : null;
        return typeof parsed?.warehouse === "string" ? parsed.warehouse : "";
    } catch {
        return "";
    }
};

// Courier colour class. Unknown codes fall back to a neutral slate so an
// 'own_label' parcel still gets a readable panel.
export const carrierClass = (code: string | null | undefined): string => {
    const c = String(code ?? "").toLowerCase();
    return ["apc", "inpost", "dpd", "cargus", "palletways", "raben", "royal_mail", "collection"].includes(c) ? `unload-carrier--${c}` : "unload-carrier--other";
};

// A courier's name for the screen when the server has not supplied one
// (a configured pallet's courier, before any parcel is scanned onto it).
const CARRIER_NAMES: Record<string, string> = {
    apc: "APC", inpost: "InPost", dpd: "DPD", cargus: "Cargus", palletways: "Palletways", raben: "Raben", own_label: "Own label",
    royal_mail: "Royal Mail", collection: "Collection",
};
export const carrierDisplayName = (code: string | null | undefined): string => {
    const c = String(code ?? "").toLowerCase();
    return CARRIER_NAMES[c] ?? c.toUpperCase();
};

const fmtTime = (iso: string | null | undefined): string => {
    if (!iso) return "";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const DispatchUnload = () => {
    usePageTitle("dispatch_unload");
    const navigate = useNavigate();
    const { t } = useTranslation();
    const warehouse = readWarehouse();

    const [step, setStep] = useState<"parcel" | "bay">("parcel");
    const [scan, setScan] = useState("");
    const [loading, setLoading] = useState(false);
    const [current, setCurrent] = useState<ParcelScan | null>(null);
    const [card, setCard] = useState<Card | null>(null);
    const [scanStatus, setScanStatus] = useState<"idle" | "success" | "error">("idle");
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const statusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // The last wrong-scan message, kept on screen after the red flash fades
    // until a good scan clears it (Alex, 2026-10-22): the flash alone was
    // gone before the operator looked up from the parcel.
    const [stickyError, setStickyError] = useState<string | null>(null);
    const [bays, setBays] = useState<Bay[] | null>(null);
    const [counts, setCounts] = useState<SessionCounts>({ byCarrier: {}, wrong: 0, aside: 0, closed: 0 });
    // Outbound pallets: the position the last parcel went on (shown on the
    // strip), the live count per open position, and close mode.
    const [lastBay, setLastBay] = useState<Bay | null>(null);
    const [openCounts, setOpenCounts] = useState<Record<string, number>>({});
    const [closing, setClosing] = useState<Closing | null>(null);
    // The pallet step is moving a parcel already on a pallet (2026-10-28):
    // `current` is the duplicate scan, confirmed with move: true.
    const [moving, setMoving] = useState(false);
    // "Print upon close" for this warehouse (GET /dispatch/unload/bays).
    const [printOnClose, setPrintOnClose] = useState(false);
    const [showPrinterSelect, setShowPrinterSelect] = useState(false);
    // The scan bar's printer button (same as Goods In › Scan Sheet): green
    // when this handheld's printer answers, red when not; null while
    // checking. Flashes on a successful print. Reprint = the last pallet
    // closed on this handheld.
    const [printerOnline, setPrinterOnline] = useState<boolean | null>(null);
    const [printFlash, setPrintFlash] = useState(false);
    const [lastClosed, setLastClosed] = useState<{ pallet: ClosedPallet; sku: string } | null>(null);

    const checkPrinter = useCallback(async () => {
        try {
            const online = getSavedNetworkPrinter() ? await isSavedNetworkPrinterOnline() : await isPrintAgentAvailable();
            setPrinterOnline(online);
        } catch {
            setPrinterOnline(false);
        }
    }, []);

    useEffect(() => {
        if (!printOnClose) return;
        checkPrinter();
        const interval = setInterval(checkPrinter, 30_000);
        return () => clearInterval(interval);
    }, [printOnClose, checkPrinter]);

    const closePrinterSelect = useCallback(() => {
        setShowPrinterSelect(false);
        checkPrinter();
    }, [checkPrinter]);

    const flash = useCallback((status: "success" | "error", message?: string) => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        setScanStatus(status);
        setErrorMessage(message);
        if (status === "success") setStickyError(null);
        else if (message) setStickyError(message);
        statusTimerRef.current = setTimeout(() => { setScanStatus("idle"); setErrorMessage(undefined); }, status === "error" ? 900 : 500);
    }, []);

    // The warehouse's courier pallets, so the page can say up front when
    // none are configured (every confirm would fail otherwise).
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/dispatch/unload/bays?warehouse=${encodeURIComponent(warehouse)}`, {});
                if (!alive) return;
                if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
                if (!res.ok) { setBays([]); return; }
                const data = await res.json();
                if (alive) {
                    setBays(Array.isArray(data.bays) ? data.bays : []);
                    setPrintOnClose(data.print_on_close === true);
                }
            } catch {
                if (alive) setBays([]);
            }
        })();
        return () => { alive = false; };
    }, [warehouse, navigate]);

    // The open pallet on each position with its live count: what "Close
    // pallet" will close. Read on entry and whenever close mode opens.
    const loadOpenPallets = useCallback(async () => {
        try {
            const res = await apiFetch(`/dispatch/unload/pallets?warehouse=${encodeURIComponent(warehouse)}`, {});
            if (!res.ok) return;
            const data = await res.json();
            const next: Record<string, number> = {};
            for (const p of (Array.isArray(data.open) ? data.open : []) as ClosedPallet[]) next[p.bay_barcode] = p.parcel_count ?? 0;
            setOpenCounts(next);
        } catch {
            /* the count is a courtesy; the close itself re-reads it */
        }
    }, [warehouse]);

    useEffect(() => { loadOpenPallets(); }, [loadOpenPallets]);

    const resetToParcel = useCallback(() => {
        setCurrent(null);
        setMoving(false);
        setStep("parcel");
    }, []);

    const scanParcel = useCallback(async (barcode: string) => {
        setLoading(true);
        try {
            const res = await apiFetch(`/dispatch/unload/scan-parcel`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ barcode }),
            });
            if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
            if (!res.ok) {
                playError();
                flash("error", t("server_error"));
                return;
            }
            const data: ParcelScan = await res.json();
            if (data.ok && data.verdict === "ok") {
                playBoxAdd();
                setStickyError(null);
                setCurrent(data);
                setStep("bay");
                return;
            }
            playError();
            if (data.verdict === "not_a_parcel") {
                // The wrong barcode (pallet label, floor barcode, routing
                // number, postcode barcode, not a label): say which and move
                // on. Nothing was recorded, and no card blocks the next scan.
                const msg: Record<string, string> = {
                    pallet_label: "unload_not_a_parcel_pallet_label",
                    floor_barcode: "unload_not_a_parcel_floor",
                    routing_number: "unload_wrong_barcode_routing",
                    qr_code: "unload_wrong_barcode_qr",
                    postcode_barcode: "unload_wrong_barcode_postcode",
                };
                flash("error", t(msg[String(data.reason)] ?? "unload_wrong_barcode"));
                return;
            }
            flash("error");
            setStickyError(null);
            setCard({ kind: data.verdict as Exclude<ParcelScan["verdict"], "ok" | "not_a_parcel">, scan: data });
        } catch {
            playError();
            flash("error", t("server_error"));
        } finally {
            setLoading(false);
        }
    }, [flash, navigate, t]);

    const confirmBay = useCallback(async (bayBarcode: string) => {
        if (!current) return;
        setLoading(true);
        try {
            const res = await apiFetch(`/dispatch/unload/confirm-bay`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ scan_id: current.scan_id, bay_barcode: bayBarcode, ...(moving ? { move: true } : {}) }),
            });
            if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
            if (!res.ok) {
                playError();
                flash("error", t("server_error"));
                return;
            }
            const data = await res.json();
            if (data.ok) {
                playSuccess();
                flash("success");
                if (data.moved_from) {
                    // Not a new parcel: the counts stay, the strip follows it.
                    const from = data.moved_from;
                    if (from.bay_barcode && from.pallet_status === "open" && Number.isFinite(Number(from.parcel_count))) {
                        setOpenCounts((m) => ({ ...m, [from.bay_barcode]: Number(from.parcel_count) }));
                    }
                    if (!data.added_to_closed && data.bay?.barcode && data.pallet) {
                        setLastBay(data.bay);
                        const n = Number(data.pallet?.parcel_count);
                        if (Number.isFinite(n)) setOpenCounts((m) => ({ ...m, [data.bay.barcode]: n }));
                    }
                    setStickyError(null);
                    setCard({
                        kind: "moved",
                        from: String(from.pallet_sku ?? from.bay_name ?? ""),
                        to: String(data.added_to_closed ? (data.pallet?.sku ?? data.pallet?.label ?? "") : (data.bay?.name ?? data.bay?.barcode ?? "")),
                    });
                    resetToParcel();
                    return;
                }
                const code = String(current.carrier_code ?? "other");
                setCounts((c) => ({ ...c, byCarrier: { ...c.byCarrier, [code]: (c.byCarrier[code] ?? 0) + 1 } }));
                if (data.added_to_closed) {
                    setStickyError(null);
                    setCard({
                        kind: "added_closed",
                        sku: String(data.pallet?.sku ?? data.pallet?.label ?? ""),
                        count: Number(data.pallet?.parcel_count) || 0,
                    });
                    resetToParcel();
                    return;
                }
                // Only a pallet-tracking position is shown on the strip.
                if (data.bay?.barcode && data.pallet) {
                    setLastBay(data.bay);
                    const n = Number(data.pallet?.parcel_count);
                    setOpenCounts((m) => ({ ...m, [data.bay.barcode]: Number.isFinite(n) ? n : (m[data.bay.barcode] ?? 0) + 1 }));
                }
                resetToParcel();
                return;
            }
            playError();
            if (data.reason === "wrong_bay") {
                flash("error");
                setStickyError(null);
                setCounts((c) => ({ ...c, wrong: c.wrong + 1 }));
                // Same courier, other pallet: name the pallet that was scanned,
                // not just the courier, or the card reads "that is APC, this
                // goes on APC".
                const scannedBayName = data.bay?.name ?? data.bay?.barcode ?? "";
                setCard({
                    kind: "wrong_bay",
                    expected: data.expected_label ?? data.expected_carrier_name ?? String(current.target_label ?? current.carrier_name ?? ""),
                    scanned: data.same_carrier ? scannedBayName : (data.bay_carrier_name ?? scannedBayName),
                    bayName: scannedBayName,
                });
                return;
            }
            if (data.reason === "pallet_loaded" && data.prior) {
                // Moving: the pallet it is on went onto the lorry meanwhile.
                flash("error", t("unload_move_loaded", { sku: String(data.prior.pallet_sku ?? data.prior.bay_name ?? "") }));
                resetToParcel();
                return;
            }
            if (data.reason === "pallet_loaded") {
                flash("error", t("unload_pallet_loaded", { sku: String(data.pallet?.print?.sku ?? data.pallet?.label ?? "") }));
                return;
            }
            if (data.reason === "same_pallet") {
                flash("error", t("unload_move_same"));
                return;
            }
            if (data.reason === "move_unavailable") {
                flash("error", t("unload_move_unavailable"));
                resetToParcel();
                return;
            }
            if (data.reason === "bay_unknown") {
                flash("error", data.is_pallet_label
                    ? t("unload_scan_floor_not_label")
                    : data.is_label
                        ? t("unload_finish_parcel_first", { carrier: String(current.target_label ?? current.carrier_name ?? "") })
                        : t("unload_not_a_pallet"));
                return;
            }
            // scan_not_loadable / already_confirmed: the scan is stale —
            // start again from the parcel.
            flash("error", t("scan_failed"));
            resetToParcel();
        } catch {
            playError();
            flash("error", t("server_error"));
        } finally {
            setLoading(false);
        }
    }, [current, moving, flash, navigate, resetToParcel, t]);

    // From the duplicate card: the parcel is already on a pallet and is
    // going on another. The next scan is the new pallet.
    const startMove = useCallback((scan: ParcelScan) => {
        setCard(null);
        setStickyError(null);
        setCurrent(scan);
        setMoving(true);
        setStep("bay");
    }, []);

    const setAside = useCallback(async () => {
        if (!current) return;
        setLoading(true);
        try {
            const res = await apiFetch(`/dispatch/unload/confirm-bay`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ scan_id: current.scan_id, set_aside: true }),
            });
            if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
            if (!res.ok) { toast.dismiss(); toast.error(t("server_error")); return; }
            setCounts((c) => ({ ...c, aside: c.aside + 1 }));
            setStickyError(null);
            setCard(null);
            resetToParcel();
        } catch {
            toast.dismiss(); toast.error(t("server_error"));
        } finally {
            setLoading(false);
        }
    }, [current, navigate, resetToParcel, t]);

    // ---- Close pallet ---------------------------------------------------

    const startClosing = useCallback(() => {
        setCard(null);
        setStickyError(null);
        // Never defaults to a position: the floor barcode is scanned twice.
        setClosing({ bay: null, count: 0 });
        loadOpenPallets();
    }, [loadOpenPallets]);

    const cancelClosing = useCallback(() => {
        setClosing(null);
        setCard(null);
        setStickyError(null);
    }, []);

    // Print a just-closed pallet's label on this handheld's printer and say
    // how it went on the card (print upon close).
    const printClosed = useCallback(async (pallet: ClosedPallet, sku: string) => {
        const base = { kind: "close_done" as const, sku, count: pallet.parcel_count ?? 0, pallet };
        if (!pallet.print) { setCard({ ...base, print: "failed" }); return; }
        setCard({ ...base, print: "printing" });
        let ok = false;
        try {
            ok = await printCourierPalletLabel(pallet.print, pallet.closed_at);
        } catch {
            ok = false;
        }
        setCard({ ...base, print: ok ? "printed" : "failed" });
        setLastClosed({ pallet, sku });
        if (ok) {
            setPrintFlash(true);
            setTimeout(() => setPrintFlash(false), 600);
        } else {
            playError();
        }
    }, []);

    const closePallet = useCallback(async (bay: Bay) => {
        setLoading(true);
        try {
            const res = await apiFetch(`/dispatch/unload/close-pallet`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ bay_barcode: bay.barcode }),
            });
            if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
            if (!res.ok) {
                playError();
                flash("error", t("server_error"));
                return;
            }
            const data = await res.json();
            if (data.ok) {
                const p = data.pallet as ClosedPallet;
                playSuccess();
                flash("success");
                setCounts((c) => ({ ...c, closed: c.closed + 1 }));
                setOpenCounts((m) => ({ ...m, [bay.barcode]: 0 }));
                setClosing(null);
                const sku = p.print?.sku ?? String(p.label ?? "");
                if (data.print_on_close === true) {
                    await printClosed(p, sku);
                } else {
                    setCard({ kind: "close_done", sku, count: p.parcel_count ?? 0, pallet: p, print: "leader" });
                }
                return;
            }
            playError();
            if (data.reason === "no_pallets") {
                flash("error", t("unload_close_no_pallets", { carrier: String(data.carrier_name ?? carrierDisplayName(bay.carrier_code)) }));
                return;
            }
            if (data.reason === "pallet_empty") {
                flash("error", t("unload_close_empty"));
                setOpenCounts((m) => ({ ...m, [bay.barcode]: 0 }));
                setClosing((c) => (c ? { ...c, count: 0 } : c));
                return;
            }
            flash("error", data.reason === "bay_unknown" ? t("unload_not_a_pallet") : t("scan_failed"));
        } catch {
            playError();
            flash("error", t("server_error"));
        } finally {
            setLoading(false);
        }
    }, [flash, navigate, printClosed, t]);

    const handleSubmit = useCallback((scannedValue?: string) => {
        const val = (scannedValue ?? scan).trim();
        setScan("");
        if (!val || loading) return;
        // A card is a verdict the operator must see; the next scan dismisses
        // it. A wrong-pallet card keeps the parcel pending, so that next
        // scan is still the pallet; every other card starts a fresh parcel.
        if (card) {
            setCard(null);
            if (card.kind !== "wrong_bay") {
                resetToParcel();
                scanParcel(val);
                return;
            }
        }
        if (closing) {
            // The first scan of a floor barcode picks (or switches) the
            // position; scanning the same one again closes it.
            const bay = (bays ?? []).find((b) => b.barcode.toUpperCase() === val.toUpperCase());
            if (bay && bay.pallets === false) {
                playError();
                flash("error", t("unload_close_no_pallets", { carrier: carrierDisplayName(bay.carrier_code) }));
                return;
            }
            if (bay && closing.bay?.barcode === bay.barcode) {
                setStickyError(null);
                closePallet(bay);
                return;
            }
            if (bay) {
                setStickyError(null);
                playBoxAdd();
                setClosing({ bay, count: openCounts[bay.barcode] ?? 0 });
                return;
            }
            playError();
            flash("error", closing.bay ? t("unload_not_a_pallet") : t("unload_close_pick_bay"));
            return;
        }
        if (step === "parcel") scanParcel(val);
        else confirmBay(val);
    }, [scan, loading, card, closing, bays, openCounts, step, scanParcel, confirmBay, closePallet, resetToParcel, flash, t]);

    const dismissCard = useCallback(() => {
        if (!card) return;
        setCard(null);
        if (card.kind !== "wrong_bay") resetToParcel();
    }, [card, resetToParcel]);

    const carrierCode = current?.carrier_code ?? null;
    const targetBays = current?.bays ?? [];
    // A move stays in the zone(s) of the position the parcel is on, so that
    // is the code shown when the parcel has none of its own.
    const targetCode = current?.sort_code ?? (moving ? current?.prior?.zones ?? null : null);
    const targetName = moving && !current?.sort_code && targetCode
        ? `${current?.carrier_name ?? ""} ${targetCode}`.trim()
        : String(current?.target_label ?? current?.carrier_name ?? "");
    // The courier + sort code IS the instruction (Alex, 2026-10-14: no list
    // of pallet names under the box); the pallet line only appears when
    // no pallet is configured for this parcel at all.
    const noBayWarning = targetBays.length === 0
        ? t("unload_no_bay_for_carrier", { carrier: targetName })
        : null;
    const closingCarrier = closing?.bay ? carrierDisplayName(closing.bay.carrier_code) : "";
    const closingName = closing?.bay ? String(closing.bay.name ?? closing.bay.barcode) : "";
    const panelCarrier = closing?.bay ? closing.bay.carrier_code : (step === "bay" && current ? carrierCode : null);

    return (
        <div
            className={`scan-panel unload-panel ${panelCarrier ? `unload-panel--carrier ${carrierClass(panelCarrier)}` : ""}`}
            style={{ backgroundColor: scanStatus === "success" ? "rgba(34, 197, 94, 0.18)" : undefined }}
        >
            <ScanFlashOverlay active={scanStatus === "error"} message={errorMessage} />
            <div className="page-title-bar">
                <h1 className="page-title">{t("dispatch_unload")}</h1>
                <button className="page-back-btn" onClick={() => navigate("/dispatch/menu")} aria-label={t("back")}>
                    <ArrowLeft size={20} />
                </button>
            </div>

            {bays !== null && bays.length === 0 && (
                <div className="unload-banner unload-banner--warn" role="status">
                    <AlertTriangle size={16} />
                    {t("unload_no_bays", { warehouse })}
                </div>
            )}

            {stickyError && scanStatus !== "error" && !card && (
                <div className="unload-banner unload-banner--error" role="alert" data-testid="unload-sticky-error">
                    <AlertTriangle size={16} />
                    {stickyError}
                </div>
            )}

            <div className="scan-centre">
                {step === "parcel" && !closing && <ScanStatusIcons status={scanStatus === "error" ? "idle" : scanStatus} />}
                {loading && <FlowLoader />}

                {closing && !loading && (
                    <div className="unload-target unload-close" data-testid="unload-close">
                        <span className="unload-target-label">{t("unload_close_title").toUpperCase()}</span>
                        {closing.bay ? (
                            <>
                                <div className="scan-command unload-target-carrier">
                                    {closingName.toUpperCase().split(" ").map((word, i) => (
                                        <FitWord key={i}>{word}</FitWord>
                                    ))}
                                </div>
                                <div className="unload-close-count" data-testid="unload-close-count">
                                    {t("unload_close_target", { carrier: closingCarrier, count: String(closing.count) })}
                                </div>
                                <span className="unload-target-hint" data-testid="unload-close-again">
                                    <ScanBarcode size={16} />
                                    {t("unload_close_scan_again")}
                                </span>
                                <span className="unload-close-sub">{t("unload_close_other_bay")}</span>
                            </>
                        ) : (
                            <span className="unload-target-hint">
                                <ScanBarcode size={16} />
                                {t("unload_close_pick_bay")}
                            </span>
                        )}
                        <button type="button" className="btn btn-ghost btn-sm unload-aside-btn" onClick={cancelClosing} disabled={loading}>
                            {t("unload_close_cancel")}
                        </button>
                    </div>
                )}

                {!closing && step === "parcel" && !loading && (
                    <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                        {t("unload_scan_parcel").toUpperCase().split(" ").map((word, i) => (
                            <FitWord key={i}>{word}</FitWord>
                        ))}
                    </div>
                )}

                {!closing && step === "bay" && current && !loading && (
                    <div className="unload-target" data-testid="unload-target">
                        <span className="unload-target-label">{t(moving ? "unload_move_to" : "unload_put_on").toUpperCase()}</span>
                        <div className="scan-command unload-target-carrier">
                            {String(current.carrier_name ?? current.carrier_code ?? "").toUpperCase().split(" ").map((word, i) => (
                                <FitWord key={i}>{word}</FitWord>
                            ))}
                        </div>
                        {targetCode && (
                            <div className="unload-target-code" data-testid="unload-sort-code" aria-label={targetName}>
                                {targetCode}
                            </div>
                        )}
                        {noBayWarning && <span className="unload-target-bay">{noBayWarning}</span>}
                        {moving && current.prior && (
                            <span className="unload-target-bay" data-testid="unload-move-from">
                                {t("unload_move_from", { from: String(current.prior.pallet_sku ?? current.prior.bay_name ?? "") })}
                            </span>
                        )}
                        <div className="unload-target-meta">
                            {current.order_number && <span className="unload-target-order">{current.order_number}</span>}
                            {current.piece && current.piece.total > 1 && (
                                <span className="unload-target-piece">
                                    {t("unload_piece_of", { position: String(current.piece.position), total: String(current.piece.total) })}
                                </span>
                            )}
                        </div>
                        <span className="unload-target-hint">
                            <ScanBarcode size={16} />
                            {t("unload_scan_pallet", { carrier: targetName })}
                        </span>
                        <span className="unload-target-hint unload-target-hint--sub">{t("unload_scan_pallet_or_label")}</span>
                        {moving ? (
                            <button type="button" className="btn btn-ghost btn-sm unload-aside-btn" onClick={resetToParcel} disabled={loading}>
                                {t("unload_move_cancel")}
                            </button>
                        ) : (
                            <button type="button" className="btn btn-ghost btn-sm unload-aside-btn" onClick={setAside} disabled={loading}>
                                <PackageX size={14} />
                                {t("unload_set_aside")}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {card && (
                <div
                    className={`unload-card-overlay unload-card-overlay--${card.kind === "close_done" || card.kind === "added_closed" || card.kind === "moved" ? "ok" : card.kind === "duplicate" ? "warn" : "stop"}`}
                    role="alertdialog"
                >
                    <div className="unload-card">
                        <h2 className="unload-card-title">
                            {card.kind === "wrong_bay" && t("unload_wrong_pallet")}
                            {card.kind === "voided" && t("unload_voided_title")}
                            {card.kind === "duplicate" && t("unload_duplicate_title")}
                            {card.kind === "unknown_label" && t("unload_unknown_title")}
                            {card.kind === "wrong_warehouse" && t("unload_wrong_warehouse_title")}
                            {card.kind === "collection" && t("unload_collection_title")}
                            {card.kind === "close_done" && t("unload_close_done")}
                            {card.kind === "added_closed" && t("unload_added_closed")}
                            {card.kind === "moved" && t("unload_moved")}
                        </h2>
                        <p className="unload-card-text">{cardText(card, t)}</p>
                        {card.kind === "wrong_bay" && (
                            <p className="unload-card-sub">{t("unload_scan_pallet", { carrier: card.expected })}</p>
                        )}
                        {card.kind === "close_done" && (
                            <p className={`unload-card-sub${card.print === "failed" ? " unload-card-sub--error" : ""}`} data-testid="unload-close-print">
                                {card.print === "leader" && t("unload_close_done_hint")}
                                {card.print === "printing" && t("unload_close_printing")}
                                {card.print === "printed" && t("unload_close_printed_hint")}
                                {card.print === "failed" && t("unload_close_print_failed")}
                            </p>
                        )}
                        <div className="unload-card-actions">
                            {card.kind === "duplicate" && card.scan.prior?.movable && card.scan.carrier_code !== "collection" && (
                                <button type="button" className="btn btn-secondary btn-sm" onClick={() => startMove(card.scan)} disabled={loading}>
                                    <ArrowRightLeft size={14} />
                                    {t("unload_move_start")}
                                </button>
                            )}
                            {card.kind === "wrong_bay" && !moving && (
                                <button type="button" className="btn btn-ghost btn-sm" onClick={setAside} disabled={loading}>
                                    {t("unload_set_aside")}
                                </button>
                            )}
                            {card.kind === "close_done" && card.print !== "leader" && (
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => printClosed(card.pallet, card.sku)}
                                    disabled={card.print === "printing"}
                                >
                                    <Printer size={14} />
                                    {t("unload_close_print_again")}
                                </button>
                            )}
                            <button type="button" className="btn btn-primary btn-sm" onClick={dismissCard}>
                                {t("unload_ok")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {!closing && step === "parcel" && !card && (
                <div className="unload-actions">
                    <button type="button" className="btn btn-secondary btn-sm unload-close-btn" onClick={startClosing} disabled={loading || !bays || bays.length === 0}>
                        <PackageCheck size={14} />
                        {t("unload_close_pallet")}
                    </button>
                </div>
            )}

            {showPrinterSelect && (
                <PrinterSelectModal
                    autoPrint={true}
                    printMode={null}
                    onSelect={closePrinterSelect}
                    onConnectLocal={closePrinterSelect}
                    onDisconnect={closePrinterSelect}
                    onClose={closePrinterSelect}
                />
            )}

            <div className="dispatch-stats-bar unload-session" aria-label={t("session_stats")}>
                <p className="dispatch-stats-title">{t("session_stats")}</p>
                <div className="dispatch-stats-row">
                    {Object.entries(counts.byCarrier).map(([code, n]) => (
                        <span key={code} className={`unload-session-chip ${carrierClass(code)}`}>{n} {code.toUpperCase()}</span>
                    ))}
                    {lastBay && (
                        <>
                            <span className="dispatch-stats-divider" />
                            <span className="dispatch-stats-item" data-testid="unload-last-pallet">
                                {lastBay.name ?? lastBay.barcode}: {t("unload_on_pallet", { count: String(openCounts[lastBay.barcode] ?? 0) })}
                            </span>
                        </>
                    )}
                    {counts.wrong > 0 && (
                        <>
                            <span className="dispatch-stats-divider" />
                            <span className="dispatch-stats-item unload-session-wrong">{counts.wrong} {t("unload_session_wrong")}</span>
                        </>
                    )}
                    {counts.aside > 0 && (
                        <>
                            <span className="dispatch-stats-divider" />
                            <span className="dispatch-stats-item">{counts.aside} {t("unload_session_aside")}</span>
                        </>
                    )}
                </div>
            </div>

            <HiddenScanInput
                value={scan}
                onChange={setScan}
                onSubmit={handleSubmit}
                disabled={loading}
                {...(printOnClose ? {
                    printConnected: printerOnline === true,
                    printFlash,
                    onPrintClick: () => setShowPrinterSelect(true),
                    canReprint: lastClosed !== null,
                    onReprintClick: () => { if (lastClosed) printClosed(lastClosed.pallet, lastClosed.sku); },
                } : {})}
            />
        </div>
    );
};

const cardText = (card: Card, t: (key: string, vars?: Record<string, string>) => string): string => {
    if (card.kind === "wrong_bay") {
        return t("unload_wrong_pallet_detail", { scanned: card.scanned || card.bayName, expected: card.expected });
    }
    if (card.kind === "moved") {
        return t("unload_moved_detail", { from: card.from, to: card.to });
    }
    if (card.kind === "close_done" || card.kind === "added_closed") {
        return t("unload_close_done_detail", { sku: card.sku, count: String(card.count) });
    }
    const s = card.scan;
    const order = String(s.order_number ?? s.order_id ?? "");
    const carrier = String(s.carrier_name ?? s.carrier_code ?? "");
    if (card.kind === "voided") {
        if (s.reason === "order_reopened") return t("unload_voided_reopened", { order });
        if (s.reason === "order_cancelled") return t("unload_voided_cancelled", { order });
        if (s.reason === "booking_cancelled") return t("unload_voided_booking", { order, carrier });
        return t("unload_voided_not_shipped", { order });
    }
    if (card.kind === "duplicate") {
        return s.prior
            ? t("unload_duplicate_detail", { bay: s.prior.bay_name ?? "", time: fmtTime(s.prior.at), name: s.prior.by ?? "" })
            : t("unload_duplicate_detail_short");
    }
    if (card.kind === "unknown_label") {
        const looks = s.looks_like ? ` ${t("unload_unknown_looks_like", { carrier: s.looks_like.toUpperCase() })}` : "";
        return `${t("unload_unknown_detail")}${looks}`;
    }
    if (card.kind === "collection") return t("unload_collection_detail", { order });
    return t("unload_wrong_warehouse_detail", { order, warehouse: String(s.order_warehouse ?? s.reason ?? "") });
};

export default DispatchUnload;
